import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import html2canvas from "html2canvas";
import FadeIn from "../compliance/FadeIn";
import DashboardHeader from "./dashboard-header";
import "./complaints-detailed.scss";
import ComplaintsSearch from "./ComplaintsSearch";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { theme } from "../../theme";
import l1Categories from "./l1categories";
import { useSearchParams } from "react-router-dom";

// Import MUI elements
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { Grid, Paper, Skeleton } from "@mui/material";
import KeyMetricsTable from "./key-metrics-table";
import Treemap from "./treemap";
import { Line } from "react-chartjs-2";
import ComplaintsRootCauses from "./rootcauses";

const Item = styled(Paper)(({ theme }) => ({
  padding: 10,
}));

const ComplaintsDetailed = ({ parentToChild }: { parentToChild: any }) => {
  const { mode } = parentToChild;
  const [searchParams, setSearchParams] = useSearchParams();
  const callReason = searchParams.get("callReason");

  const [flag, setFlag] = useState<boolean>(false);
  const [keyMetricsData, setKeyMetricsData] = useState<any>(null);
  const [forceSearchInputValue, setForceSearchInputValue] =
    useState<string>("");
  const [selectedL1Category, setSelectedL1Category] = useState<string>("");
  const [selectedL2Category, setSelectedL2Category] = useState<string>("");
  const [lineChartData, setLineChartData] = useState<any>(null);

  const [rootCauses, setRootCauses] = useState<any[]>([]);
  const [rootCausesLoading, setRootCausesLoading] = useState<boolean>(true);
  const [l3Categories, setL3Categories] = useState<any>(null);
  const [l3CategoriesLoading, setL3CategoriesLoading] = useState<boolean>(true);

  // Add state for selected option and start date
  const [selectedOption, setSelectedOption] = useState<string>("Last 7 days");
  const [endDate, setEndDate] = useState<dayjs.Dayjs>(dayjs("2024-01-31"));
  const [startDate, setStartDate] = useState<dayjs.Dayjs>(dayjs("2024-01-25"));

  // Find the selected L1 category from the l1Categories array
  const selectedL1 = l1Categories.find(
    (category) => category.slug === callReason
  );

  const selectedL2 = selectedL1
    ? selectedL1.l2Categories.find(
        (subCategory) => subCategory.slug === selectedL2Category
      )
    : null;

  const onChangeL1Category = (categorySlug: any) => {
    setSearchParams((params) => {
      params.set("callReason", categorySlug);
      return params;
    });
  };

  useEffect(() => {
    setSelectedL1Category(callReason || "fees");
  }, [callReason]);

  // Automatically select the first L2 category when L1 changes
  useEffect(() => {
    if (selectedL1Category) {
      setSelectedL2Category("all");
      setFlag((flag) => !flag);
    }
  }, [selectedL1Category]);

  useEffect(() => {
    setFlag((flag) => !flag);
  }, [selectedL2Category]);

  // fetch tree map data
  useEffect(() => {
    const fetchData = async () => {
      setL3CategoriesLoading(true);
      try {
        const formattedStartDate = startDate.toISOString().split("T")[0];
        const formattedEndDate = endDate.toISOString().split("T")[0];

        // Fetch root_causes data
        const l3CategoriesResponse = await fetch(
          `/l3_categories?start_date=${formattedStartDate}&end_date=${formattedEndDate}&l1_category=${
            selectedL1?.displayName
          }&l2_category=${
            selectedL2Category !== "all" ? selectedL2?.displayName : ""
          }`
        );
        const l3CategoriesData = await l3CategoriesResponse.json();

        setL3Categories(l3CategoriesData);
        setL3CategoriesLoading(false);
      } catch (error) {
        setL3CategoriesLoading(false);
        console.error("Error fetching data:", error);
      }
    };

    selectedL2Category && fetchData();
  }, [flag, startDate, endDate]);

  // Set screenshot data for send report button
  const [screenshot, setScreenshot] = useState<any>();

  useEffect(() => {
    const fetchData = async () => {
      setRootCausesLoading(true);
      try {
        const formattedStartDate = startDate.toISOString().split("T")[0];
        const formattedEndDate = endDate.toISOString().split("T")[0];

        // Fetch root_causes data
        const rootCausesResponse = await fetch(
          `/root_causes?start_date=${formattedStartDate}&end_date=${formattedEndDate}&l1_category=${
            selectedL1?.displayName
          }&l2_category=${
            selectedL2Category !== "all" ? selectedL2?.displayName : ""
          }`
        );
        const rootCausesData = await rootCausesResponse.json();

        setRootCauses(rootCausesData);
        setRootCausesLoading(false);
      } catch (error) {
        setRootCausesLoading(false);
        console.error("Error fetching data:", error);
      }
    };

    selectedL2Category && fetchData();
  }, [flag, startDate, endDate]);

  // Updated DatePicker onChange handler
  const handleEndDateChange = (
    selectedOption: string,
    newDate: dayjs.Dayjs
  ) => {
    // Calculate updatedStartDate asynchronously
    setEndDate(newDate);
    let calculatedStartDate;

    const calculateUpdatedStartDate = () => {
      calculatedStartDate = newDate;

      if (selectedOption === "Last 7 days" && newDate instanceof dayjs) {
        calculatedStartDate = newDate.subtract(6, "days");
      } else if (
        selectedOption === "Last 30 days" &&
        newDate instanceof dayjs
      ) {
        calculatedStartDate = newDate.subtract(30, "days");
      } else if (
        selectedOption === "Last 60 days" &&
        newDate instanceof dayjs
      ) {
        calculatedStartDate = newDate.subtract(60, "days");
      }
      setStartDate(calculatedStartDate);
    };
    selectedL2Category && calculateUpdatedStartDate();
  };

  const handleReset = () => {
    setSelectedOption("Last 7 days");
    setEndDate(dayjs("2024-01-31"));
    setStartDate(dayjs("2024-01-24"));
    onChangeL1Category("fees");
  };

  const onButtonClick = async () => {
    const canvas = await html2canvas(document.body);
    const dataUrl = canvas.toDataURL("image/png");

    setScreenshot(dataUrl);
  };

  // Get key metrics data
  useEffect(() => {
    const formattedStartDate = startDate.toISOString().split("T")[0];
    const formattedEndDate = endDate.toISOString().split("T")[0];

    const fetchData = async () => {
      try {
        const response = await fetch(
          `/volume_and_metrics?start_date=${formattedStartDate}&end_date=${formattedEndDate}&l1_category=${
            selectedL1?.displayName
          }&l2_category=${
            selectedL2Category !== "all" ? selectedL2?.displayName : ""
          }`
        );
        const result = await response.json();
        setKeyMetricsData(result);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    selectedL2Category && fetchData();
  }, [flag, startDate, endDate]);

  // Get complaints line chart data
  useEffect(() => {
    const formattedStartDate = startDate.toISOString().split("T")[0];
    const formattedEndDate = endDate.toISOString().split("T")[0];

    const fetchData = async () => {
      setLineChartData(null);
      try {
        const response = await fetch(
          `/daily_counts?start_date=${formattedStartDate}&end_date=${formattedEndDate}&l1_category=${
            selectedL1?.displayName
          }&l2_category=${
            selectedL2Category !== "all" ? selectedL2?.displayName : ""
          }`
        );
        const result = await response.json();

        const countArray = result.map((item: any) => parseFloat(item.count));
        const l1CategoryArray = result.map((item: any) => item.l1_category);
        const dateArray = result.map((item: any) => {
          const date = new Date(item.date);
          const day = ("0" + date.getDate()).slice(-2);
          const monthNames = [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
          ];
          const month = monthNames[date.getMonth()];
          return `${day}-${month}`;
        });

        setLineChartData([l1CategoryArray, countArray, dateArray]);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    selectedL2Category && fetchData();
  }, [flag, startDate, endDate]);

  const options4 = {
    maintainAspectRatio: false,
    responsive: true,
    indexAxis: "x" as const,
    elements: {
      bar: {
        borderWidth: 2,
      },
    },
    plugins: {
      datalabels: {
        display: false,
        color: mode === "light" ? "black" : "white",
        zIndex: 5,
        labels: {
          value: {
            color: mode === "light" ? "black" : "white",
          },
        },
        formatter: function (context: any) {
          let format = context.toFixed(0);
          return format;
        },
      },
      legend: {
        display: false,
        labels: {
          boxWidth: 10,
          color: mode === "light" ? "#797979" : "#b3b3b3",
        },
      },
      title: {
        display: false,
      },
    },
    scales: {
      x: {
        title: {
          display: false,
        },
        ticks: {
          color: mode === "light" ? "#797979" : "#b3b3b3",
        },
        grid: {
          color: mode === "light" ? "#e4e4e4" : "#5e5e5e",
        },
      },
      y: {
        title: {
          display: false,
        },
        ticks: {
          color: mode === "light" ? "#797979" : "#b3b3b3",
        },
        grid: {
          color: mode === "light" ? "#e4e4e4" : "#5e5e5e",
        },
      },
    },
  };

  // Combined function to get both filtered data and labels
  const getFilteredDataAndLabels = () => {
    // Check if lineChartData exists and is valid
    if (
      !lineChartData ||
      !Array.isArray(lineChartData) ||
      lineChartData.length < 3
    ) {
      return { data: [], labels: [] }; // Return empty arrays if there's no valid data
    }

    const [l1CategoryArray, countArray, dateArray] = lineChartData;

    // If 'All' is selected, return all data and the corresponding labels
    // if (selectedFilter === "All") {
    return {
      data: countArray.slice(0, 7), // Get the first 7 data points
      labels: dateArray.slice(0, 7), // Get the first 7 date labels
    };
  };

  // Extract data and labels from the combined function
  const { data: filteredData, labels: filteredLabels } =
    getFilteredDataAndLabels();

  // Use the filtered data and labels in your chart configuration
  const data4 = {
    labels: filteredLabels, // Use the filtered labels
    datasets: [
      {
        label: selectedL2Category,
        data: filteredData, // Use the filtered data
        borderColor: theme.colors.primary,
        backgroundColor: "#2e2e38",
      },
    ],
  };

  const transformedL3Categories = l3Categories?.map((category: any) => ({
    x: category.l3_category, // Change 'l3_category' to 'x'
    y: category.count, // Change 'count' to 'y'
    textToDisplay: `Details about ${category.l3_category}`, // Add new field
  }));

  return (
    <FadeIn transitionDuration={700}>
      <div className='ops-root container'>
        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <DashboardHeader
                handleReset={handleReset}
                handleEndDateChange={handleEndDateChange}
                endDate={endDate}
                // setClickedLabel={setClickedLabel}
                setStartDate={setStartDate}
                setSelectedOption={setSelectedOption}
                selectedOption={selectedOption}
                onButtonClick={onButtonClick}
                // openModal={openModal}
              />
              <FadeIn transitionDuration={700}>
                <Grid className='ops-container-wrapper' container spacing={2}>
                  <Grid item xs={12} sm={12} md={12} lg={12} xl={6}>
                    <Item className='complaints-detailed-items-wrapper'>
                      <Grid item xs={12}>
                        <FormControl
                          variant='standard'
                          sx={{ m: 1, minWidth: 250 }}
                        >
                          <InputLabel id='demo-simple-select-standard-label'>
                            Complaint Reason
                          </InputLabel>
                          <Select
                            labelId='demo-simple-select-standard-label'
                            id='demo-simple-select-standard'
                            value={selectedL1Category}
                            onChange={(e) => onChangeL1Category(e.target.value)}
                          >
                            {/* <MenuItem value='All'>All</MenuItem> */}
                            {l1Categories?.map((category) => (
                              <MenuItem
                                key={category?.id}
                                value={category?.slug}
                              >
                                {category?.displayName}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControl
                          variant='standard'
                          sx={{ m: 1, minWidth: 250 }}
                        >
                          <InputLabel id='demo-simple-select-standard-label'>
                            Complaint Sub-Reason
                          </InputLabel>
                          <Select
                            labelId='demo-simple-select-standard-label'
                            id='demo-simple-select-standard'
                            value={selectedL2Category}
                            onChange={(e) => {
                              setSelectedL2Category(e.target.value);
                            }}
                          >
                            <MenuItem value='all'>All</MenuItem>
                            {selectedL1?.l2Categories?.map((category: any) => (
                              <MenuItem
                                key={category?.id}
                                value={category?.slug}
                              >
                                {category?.displayName}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <KeyMetricsTable keyMetricsData={keyMetricsData} />
                      </Grid>
                      <div className='complaints-detailed-chart-table-container'>
                        <Grid
                          item
                          xs={12}
                          sm={12}
                          md={12}
                          lg={6}
                          xl={6}
                          sx={{ marginTop: "10px" }}
                        >
                          <FadeIn transitionDuration={700}>
                            <React.StrictMode>
                              <div className='complaint-detailed-chart-wrapper'>
                                <div className='complaint-chart-header-wrapper'>
                                  <h3>Complaint Volume</h3>
                                </div>
                                <div
                                  className='complaint-chart-2'
                                  style={{ paddingTop: "20px" }}
                                >
                                  <Line
                                    options={options4}
                                    data={data4}
                                    plugins={[ChartDataLabels]}
                                  />
                                </div>
                              </div>
                            </React.StrictMode>
                          </FadeIn>
                        </Grid>
                        <ComplaintsRootCauses
                          rootCauses={rootCauses}
                          rootCausesLoading={rootCausesLoading}
                          mode={mode}
                          onRootCauseClick={(rootCause: string) =>
                            setForceSearchInputValue(rootCause)
                          }
                        />
                      </div>
                    </Item>
                  </Grid>
                  <Grid item xs={12} sm={12} md={12} lg={12} xl={6}>
                    <Item className='complaints-detailed-items-wrapper'>
                      <Treemap
                        treeData={
                          l3Categories &&
                          transformedL3Categories &&
                          transformedL3Categories.length
                            ? [{ data: transformedL3Categories }]
                            : null
                        }
                        isLoading={l3CategoriesLoading}
                        onSelectCustomerIntent={(customerIntent: string) => {
                          setForceSearchInputValue(
                            `call regarding ${customerIntent}`
                          );
                        }}
                        mode={mode}
                        startDate={startDate}
                        endDate={endDate}
                      />
                    </Item>
                  </Grid>
                </Grid>
              </FadeIn>
              <FadeIn>
                <Grid item xs={12} style={{ paddingTop: "20px" }}>
                  <Item className='complaints-detailed-semantic-search-card'>
                    <div className='complaints-detailed-search-header'>
                      <h3>Transcript Semantic Search</h3>
                    </div>
                    <React.StrictMode>
                      <ComplaintsSearch
                        parentToChild={{
                          startDate,
                          endDate,
                          selectedOption,
                          mode,
                          forceSearchInputValue,
                        }}
                      />
                    </React.StrictMode>
                  </Item>
                </Grid>
              </FadeIn>
            </Grid>
          </Grid>
        </Box>
      </div>
    </FadeIn>
  );
};

export default ComplaintsDetailed;
